import { inject, injectable } from "tsyringe";
import { Msal } from "src/containers/authentication/services/msal/msal-service";
import { OtpService } from "src/containers/otp/services/otp-service/otp-service";
import { PaymentSummaryService } from "src/containers/payments/services/payment-summary/payment-summary";
import { SurveysService } from "src/containers/surveys/services/surveys-service/surveys-service";
import { DI } from "src/infra/di/container";
import { MobileUtils } from "src/libs/mobile";
import { Platform } from "src/libs/platform";
import { CookieStorageService } from "src/shared/services/cookie-storage/cookie-storage-service";
import { User } from "src/shared/models/user.model";
import { Token } from "src/containers/authentication/models/token";
import { autoBind } from "src/shared/decorators/autobind";

@injectable()
export class SessionService {
    public static readonly KEY_AUTH_TOKEN = "auth-token-client";
    public static readonly KEY_USER_DATA = "auth-data-client";
    public static readonly KEY_MSAL_TOKEN = "msal.idtoken";
    public static readonly KEY_REDIRECT_AFTER_PUSH = "REDIRECT_AFTER_PUSH";
    public readonly msal: Msal;

    constructor(
        @inject("CookieStorageService") private storage: CookieStorageService
    ) {
        this.msal = Msal.getInstance(storage);
    }

    @autoBind
    postAuthentication(token: Token, user: User, cb = () => {}) {
        if (Platform.isRunningOnFlutter()) {
            localStorage.setItem("MOBILE_APPLICATION", "true");
        }
        this.setToken(token);
        this.saveUser(user, token.expiresIn);
        cb();
    }

    @autoBind
    postLogout(doMsalLogout = false) {
        const surveysService = DI.get(SurveysService);
        const paymentServie = DI.get(PaymentSummaryService);
        const otpService = DI.get(OtpService);
        paymentServie.removeHashRedirect();
        otpService.removeData();
        this.removeEllapsedTime();
        this.removeToken();
        this.removeUser();
        sessionStorage.clear(); // para asegurar xD
        surveysService.clear();

        if (Platform.isRunningOnFlutter()) {
            localStorage.removeItem("MOBILE_APPLICATION");
            MobileUtils.logoutMobile();
        } else {
            // eslint-disable-next-line no-lonely-if
            if (doMsalLogout) {
                /* We need to navigate first to index because MSal takes
                    the current url as redirect url, and B2C register domains only */
                setTimeout(() => {
                    window.history.pushState(null, document.title, "/");
                    this.msal.logout();
                }, 1);
            }
        }
    }

    @autoBind
    getB2CToken(): string | null {
        return sessionStorage.getItem(SessionService.KEY_MSAL_TOKEN) || null;
    }

    @autoBind
    getToken(): Token | null {
        try {
            const json = this.storage.getCookie<object>(
                SessionService.KEY_AUTH_TOKEN
            );
            if (json) {
                return Token.fromJson<Token>(json);
            }
            return null;
        } catch {
            return null;
        }
    }

    @autoBind
    setToken(token: Token): void {
        this.storage.setCookie(
            SessionService.KEY_AUTH_TOKEN,
            token.toJson(),
            token.expiresIn
        );
    }

    @autoBind
    removeToken(): void {
        this.storage.removeCookie(SessionService.KEY_MSAL_TOKEN);
        this.storage.removeCookie(SessionService.KEY_AUTH_TOKEN);
    }

    @autoBind
    getAuthorization(): string {
        const token = this.getToken() as Token;
        return `${token?.type} ${token?.value}`;
    }

    @autoBind
    isSessionExpired(): boolean {
        const token = this.storage.getCookie<Token>(
            SessionService.KEY_AUTH_TOKEN
        );
        return !token;
    }

    @autoBind
    updateEllapsedTime(time: number) {
        localStorage.setItem("SESSION_ELLAPSED_TIME", time.toString());
    }

    @autoBind
    getEllapsedTime(): number | null {
        const value = localStorage.getItem("SESSION_ELLAPSED_TIME");
        return value ? Number.parseInt(value) : null;
    }

    @autoBind
    removeEllapsedTime() {
        localStorage.removeItem("SESSION_ELLAPSED_TIME");
    }

    @autoBind
    saveUser(user: User, expiration: number) {
        this.storage.setCookie(
            SessionService.KEY_USER_DATA,
            user.toJson(),
            expiration
        );
    }

    @autoBind
    getUser(): User | null {
        const data = this.storage.getCookie<object>(
            SessionService.KEY_USER_DATA
        );
        if (data) {
            return User.fromJson<User>(data);
        }
        return null;
    }

    @autoBind
    removeUser(): void {
        this.storage.removeCookie(SessionService.KEY_USER_DATA);
    }

    @autoBind
    saveRedirectUrlAfterPushN(pathname: string) {
        sessionStorage.setItem(
            SessionService.KEY_REDIRECT_AFTER_PUSH,
            pathname
        );
    }
}
